import PolarisIcon from '@/components/_common/PolarisIcon';
import { Accordion, AccordionDetails, AccordionSummary, Button, styled, Typography } from '@mui/material';
import { FC, MouseEvent, useEffect, useState } from 'react';
import { FocusTheme } from '../../../configs/theme';
import NewScheduleAvailItem, { NewScheduleAvailItemChangeType } from './NewScheduleAvailItem';

export type GroupRights = {
  label: string;
  territories: string[];
  selectedCount?: number;
  addedCount?: number;
  removedCount?: number;
};

type TerritoryGroupedAvailListProps = {
  groups: GroupRights[];
  hasRights?: (code: string) => boolean;
  isSelected?: (code: string) => boolean;
  onItemClick?: (code: string) => void;
  onItemsClick: (codes: string[]) => void;
  variant?: 'default' | 'removable';
  formatDisplayLabel: (code: string) => string;
  scrollToElementName?: string;
  getItemChangeType?: (code: string) => NewScheduleAvailItemChangeType;
  showCheckbox?: boolean;
  hideSelectAll?: boolean;
};

const getRegionLabel = (count: number) => `${count} ${count === 1 ? 'region' : 'regions'}`;

const TerritoryGroupedAvailList: FC<TerritoryGroupedAvailListProps> = ({
  groups,
  hasRights,
  isSelected,
  onItemClick,
  onItemsClick,
  variant = 'default',
  formatDisplayLabel,
  scrollToElementName,
  getItemChangeType,
  showCheckbox = false,
  hideSelectAll = false
}) => {
  const [expandedGroup, setExpandedGroup] = useState<string | false>(scrollToElementName || false);

  useEffect(() => {
    if (scrollToElementName) {
      setExpandedGroup(scrollToElementName);
    }
  }, [scrollToElementName]);

  return (
    <div>
      {groups.map((group) => {
        const handleGroupSelectAll = (event: MouseEvent<HTMLButtonElement>) => {
          event.stopPropagation();
          if (!group.territories.length) return;
          onItemsClick(group.territories);
        };

        const handleAccordionChange = (_event: unknown, isExpanded: boolean) => {
          setExpandedGroup(isExpanded ? group.label : false);
        };

        const summaryCount = group.selectedCount ?? group.territories.length;
        const isExpanded = expandedGroup === group.label;
        const hasDelta = !!group.addedCount || !!group.removedCount;

        const deltaParts: string[] = [];
        if (group.addedCount) {
          deltaParts.push(`+${group.addedCount}`);
        }
        if (group.removedCount) {
          deltaParts.push(`-${group.removedCount}`);
        }

        return (
          <TerritoryAccordion disableGutters key={group.label} expanded={isExpanded} onChange={handleAccordionChange}>
            <TerritoryAccordionSummary data-scroll-to-element-name={group.label} expandIcon={<ExpandIcon icon="down_filled" />}>
              <StyledGroupLabel>{group.label}</StyledGroupLabel>
              <StyledGroupCount>{getRegionLabel(summaryCount)}</StyledGroupCount>
              <StyledSummaryRight>
                {hasDelta && (
                  <StyledDeltaText variant="body2" $isExpanded={isExpanded}>
                    ({deltaParts.join(', ')})
                  </StyledDeltaText>
                )}
                {variant === 'default' && !hideSelectAll && (
                  <SelectAllButton onClick={handleGroupSelectAll} variant="text">
                    Select all
                  </SelectAllButton>
                )}
              </StyledSummaryRight>
            </TerritoryAccordionSummary>
            <TerritoryAccordionDetails>
              <ExpandedList>
                {group.territories.map((territory) => (
                  <NewScheduleAvailItem
                    key={territory}
                    onClick={() => onItemClick?.(territory)}
                    primaryLabel={formatDisplayLabel(territory)}
                    type="success"
                    variant={variant}
                    isSelectedItem={!!isSelected?.(territory)}
                    hasRights={!!hasRights?.(territory)}
                    changeType={getItemChangeType?.(territory)}
                    showCheckbox={showCheckbox}
                  />
                ))}
              </ExpandedList>
            </TerritoryAccordionDetails>
          </TerritoryAccordion>
        );
      })}
    </div>
  );
};

export default TerritoryGroupedAvailList;

const ExpandedList = styled('div')`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.5)};
`;

const TerritoryAccordion = styled(Accordion)(({ theme }) => ({
  '&.MuiAccordion-root': { marginBottom: theme.spacing(0.5) },
  '& .MuiAccordionDetails-root': { padding: theme.spacing(2) }
}));

const TerritoryAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  '&.MuiAccordionSummary-root.MuiAccordionSummary-root': {
    padding: `0 ${theme.spacing(2)}`,
    flexDirection: 'row',
    minHeight: '55px',
    backgroundColor: (theme as FocusTheme).palette.neutral[80],
    borderRadius: 6,
    '&.Mui-expanded': {
      borderBottomRightRadius: 0,
      borderBottomLeftRadius: 0,
      backgroundColor: (theme as FocusTheme).palette.neutral[70]
    },
    '&.Mui-disabled': {
      opacity: 1,
      backgroundColor: (theme as FocusTheme).palette.neutral[80],
      color: (theme as FocusTheme).palette.neutral[40],
      '& .MuiAccordionSummary-expandIconWrapper': {
        color: (theme as FocusTheme).palette.neutral[40]
      }
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
      marginLeft: theme.spacing(1)
    },
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
      transform: 'rotate(180deg)',
      color: theme.palette.secondary.main
    },
    '& .MuiAccordionSummary-content': {
      display: 'grid',
      gridTemplateColumns: '1.25fr auto 1fr',
      alignItems: 'center',
      margin: 0,
      width: '100%',
      minWidth: 0
    }
  }
}));

const TerritoryAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  '&.MuiAccordionDetails-root': {
    padding: theme.spacing(2),
    backgroundColor: (theme as FocusTheme).palette.neutral[70],
    borderBottomRightRadius: 6,
    borderBottomLeftRadius: 6
  }
}));

const StyledGroupLabel = styled(Typography)`
  margin-right: ${({ theme }) => theme.spacing(1)};
`;

const StyledGroupCount = styled(Typography)(({ theme }) => ({
  justifySelf: 'center',
  whiteSpace: 'nowrap',
  color: (theme as FocusTheme).palette.neutral[30],
  fontSize: theme.typography.pxToRem(14)
}));

const StyledSummaryRight = styled('div')`
  display: flex;
  align-items: center;
  justify-self: end;
`;

const StyledDeltaText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== '$isExpanded'
})<{ $isExpanded: boolean }>(({ theme, $isExpanded }) => ({
  whiteSpace: 'nowrap',
  marginRight: theme.spacing(1),
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 500,
  color: $isExpanded ? theme.palette.text.primary : (theme as FocusTheme).palette.neutral[20]
}));

const SelectAllButton = styled(Button)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 500,
  whiteSpace: 'nowrap'
}));

const ExpandIcon = styled(PolarisIcon)(() => ({
  height: 10,
  width: 10
}));
